"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import { PublicBannerItem } from "@/lib/banners/types";

const DEFAULT_ROTATION_INTERVAL_MS = 10_000; // 10 seconds
const DEFAULT_TRANSITION_DURATION_MS = 600;

type UseRotatingBannersParams = {
  banners: PublicBannerItem[];
  intervalMs?: number;
  transitionDurationMs?: number;
  enabled?: boolean;
};

/**
 * Cycles through a list of banners on a fixed interval.
 *
 * - With 0 or 1 banner it is a no-op: the single banner is returned and
 *   `isRotating` is false so the consumer renders it statically.
 * - With 2+ banners it advances through a transition phase (outgoing + incoming)
 *   before committing the next active index.
 */
export const useRotatingBanners = ({
  banners,
  intervalMs = DEFAULT_ROTATION_INTERVAL_MS,
  transitionDurationMs = DEFAULT_TRANSITION_DURATION_MS,
  enabled = true,
}: UseRotatingBannersParams) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [incomingIndex, setIncomingIndex] = useState<number | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const activeIndexRef = useRef(0);
  const isTransitioningRef = useRef(false);

  const bannerCount = banners.length;
  const bannerIds = banners.map((banner) => banner.bannerId).join("|");
  const shouldRotate = enabled && bannerCount > 1;

  const outgoingIndex = isTransitioning ? activeIndex : null;

  useEffect(() => {
    setActiveIndex(0);
    activeIndexRef.current = 0;
    setIncomingIndex(null);
    setIsTransitioning(false);
    isTransitioningRef.current = false;
  }, [bannerIds]);

  const startTransition = useCallback(() => {
    if (bannerCount <= 1 || isTransitioningRef.current) {
      return;
    }

    const nextIndex = (activeIndexRef.current + 1) % bannerCount;
    isTransitioningRef.current = true;
    setIncomingIndex(nextIndex);
    setIsTransitioning(true);
  }, [bannerCount]);

  useEffect(() => {
    if (!shouldRotate) {
      return;
    }

    const intervalId = setInterval(startTransition, intervalMs);
    return () => clearInterval(intervalId);
  }, [shouldRotate, intervalMs, startTransition]);

  useEffect(() => {
    if (!isTransitioning || incomingIndex === null) {
      return;
    }

    const prefersReducedMotion =
      typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const duration = prefersReducedMotion ? 0 : transitionDurationMs;

    const timeoutId = setTimeout(() => {
      setActiveIndex(incomingIndex);
      activeIndexRef.current = incomingIndex;
      setIncomingIndex(null);
      setIsTransitioning(false);
      isTransitioningRef.current = false;
    }, duration);

    return () => clearTimeout(timeoutId);
  }, [incomingIndex, isTransitioning, transitionDurationMs]);

  const activeBanner = banners[activeIndex] ?? banners[0] ?? null;

  return {
    activeBanner,
    activeIndex,
    outgoingIndex,
    incomingIndex,
    isTransitioning,
    isRotating: shouldRotate,
  };
};
